import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { CarrierService } from "@/api.types.v2";
import type { Carrier } from "@/api/carriers";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Pricing-redesign: list normalized CarrierService rows for admin pickers.
 *
 * Returned rows include the parent `carrier` (id/code/name/selfRated) so dropdowns
 * can render "Carrier name — Service name" without a second fetch, and so a screen
 * can tell whether EZX prices this carrier itself.
 */
export type CarrierServiceListItem = CarrierService & {
  carrier: Pick<Carrier, "id" | "code" | "name" | "selfRated">;
};

export function useGetCarrierServices(params?: {
  active?: boolean;
  carrierId?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-services", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.active !== undefined) {
        queryParams.append("active", String(params.active));
      }
      if (params?.carrierId) queryParams.append("carrierId", params.carrierId);

      const { data } = await axios.get<{ data: CarrierServiceListItem[] }>(
        `${API_URL}/api/v2/admin/carrier-services${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
    staleTime: 60_000, // CarrierService rows change rarely; cache for a minute
  });
}

/**
 * Format a CarrierService row for display in dropdowns / tables.
 *   "EZ Fleet — Same Day Delivery"
 */
export function formatCarrierServiceLabel(
  cs: CarrierServiceListItem | undefined
): string {
  if (!cs) return "";
  return `${cs.carrier.name} — ${cs.name}`;
}

/**
 * PATCH a CarrierService. Phase 8 covered dim factors (pricing workspace);
 * Phase 15c adds `isActive` so the Services tab can flip status via a
 * Switch instead of having to go through the one-way DELETE soft-disable.
 */
export interface UpdateCarrierServiceInput {
  estimatedDays?: number;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
  isActive?: boolean;
  /** Phase C (#265): package fit ceilings. NULL clears, omit leaves alone. */
  maxWeightKg?: number | null;
  maxLongestSideCm?: number | null;
  maxLengthPlusGirthCm?: number | null;
}

export function useUpdateCarrierService(options?: {
  onSuccess?: (data: CarrierServiceListItem) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateCarrierServiceInput;
    }): Promise<CarrierServiceListItem> => {
      const { data } = await axios.patch<{ data: CarrierServiceListItem }>(
        `${API_URL}/api/v2/admin/carrier-services/${id}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-services"] });
      void queryClient.invalidateQueries({ queryKey: ["carriers"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}

/**
 * Phase 14: manual creation of a CarrierService row. OWNED carriers always
 * use this path; external carriers use it as an override when discovery
 * doesn't pick up a service the admin knows exists.
 */
export interface CreateCarrierServiceInput {
  code: string;
  name: string;
  estimatedDays?: number | null;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
}

export function useCreateCarrierService(options?: {
  onSuccess?: (data: CarrierServiceListItem) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      input,
    }: {
      carrierId: string;
      input: CreateCarrierServiceInput;
    }): Promise<CarrierServiceListItem> => {
      const { data } = await axios.post<{ data: CarrierServiceListItem }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/services`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-services"] });
      void queryClient.invalidateQueries({ queryKey: ["carriers"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}

/**
 * Phase 14: warn-and-allow soft-delete. Backend returns 409 with a count of
 * referencing rate bands + open shipments when references exist; pass
 * `confirm=true` to override.
 */
export interface DeleteCarrierServiceConflictDetails {
  activeRateBandCount: number;
  openShipmentCount: number;
  message: string;
}

export function useDeleteCarrierService(options?: {
  onSuccess?: (data: CarrierServiceListItem) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      confirm = false,
    }: {
      id: string;
      confirm?: boolean;
    }): Promise<CarrierServiceListItem> => {
      const url = `${API_URL}/api/v2/admin/carrier-services/${id}${
        confirm ? "?confirm=true" : ""
      }`;
      const { data } = await axios.delete<{ data: CarrierServiceListItem }>(
        url,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-services"] });
      void queryClient.invalidateQueries({ queryKey: ["carriers"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}

/**
 * Phase 14: per-carrier discovery aggregator. Returns merged candidates
 * across active integration purchasing options.
 */
export interface DiscoveryCandidate {
  code: string;
  name: string;
  estimatedDays: number | null;
  /** SupplyChannel IDs; the wire name is retained by the backend. */
  sourceProviderIds: string[];
  alreadyExists: boolean;
  existingServiceId: string | null;
}

export interface DiscoveryChannelResult {
  channelId: string;
  providerId: string;
  providerName: string;
  serviceCount: number;
  error?: string;
}

export interface AggregateDiscoveryResult {
  candidates: DiscoveryCandidate[];
  channels: DiscoveryChannelResult[];
}

export function useDiscoverServicesAggregate(options?: {
  onSuccess?: (data: AggregateDiscoveryResult) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      carrierId,
    }: {
      carrierId: string;
    }): Promise<AggregateDiscoveryResult> => {
      const { data } = await axios.post<{ data: AggregateDiscoveryResult }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/services/discover`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}

/**
 * Phase 14: idempotent batch upsert from discovery candidates.
 */
export interface ApplyCandidateInput {
  code: string;
  name: string;
  estimatedDays?: number | null;
  /** SupplyChannel IDs; the wire name is retained by the backend. */
  sourceProviderIds: string[];
}

export interface ApplyDiscoveryResult {
  createdCount: number;
  updatedCount: number;
  createdIds: string[];
  updatedIds: string[];
}

export function useApplyDiscoveryCandidates(options?: {
  onSuccess?: (data: ApplyDiscoveryResult) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      candidates,
    }: {
      carrierId: string;
      candidates: ApplyCandidateInput[];
    }): Promise<ApplyDiscoveryResult> => {
      const { data } = await axios.post<{ data: ApplyDiscoveryResult }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/services/apply`,
        { candidates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-services"] });
      void queryClient.invalidateQueries({ queryKey: ["carriers"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}
